import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { schedulerAPI, sedesAPI, assignmentsAPI } from '../../services/api';
import { Calendar, Play, Loader2, CheckCircle, AlertCircle, Users, MapPin, Layers, Maximize2, Minimize2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

const DAY_NAMES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const STATUS_STYLES = {
  assigned: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show: 'bg-slate-100 text-slate-700',
};

const SchedulerPage = () => {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [detailed, setDetailed] = useState([]);
  const [hasSimulation, setHasSimulation] = useState(false);
  const [loadingDetailed, setLoadingDetailed] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    loadSedes();
    const today = new Date();
    const currentMonday = startOfWeek(today, { weekStartsOn: 1 });
    setWeekStartDate(format(currentMonday, 'yyyy-MM-dd'));
  }, []);

  useEffect(() => {
    if (selectedSede && weekStartDate) {
      loadDetailed();
      setHasSimulation(false);
      setResult(null);
    }
  }, [selectedSede, weekStartDate]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) setSelectedSede(response.data[0].id);
    } catch (error) {
      console.error('Error loading sedes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailed = async () => {
    setLoadingDetailed(true);
    try {
      const response = await assignmentsAPI.getDetailed({ sede_id: selectedSede, week_start_date: weekStartDate });
      setDetailed(response.data);
    } catch (error) {
      console.error('Error loading detailed:', error);
      setDetailed([]);
    } finally {
      setLoadingDetailed(false);
    }
  };

  const handleSimulate = async () => {
    setSimulating(true);
    try {
      const response = await schedulerAPI.execute({
        sede_id: selectedSede,
        week_start_date: weekStartDate,
        is_simulation: true
      });
      setResult(response.data);
      if (response.data.simulation_details) {
        setDetailed(response.data.simulation_details);
        setHasSimulation(true);
      }
      toast.success('Simulación completada — presiona "Ejecutar" para guardar esta distribución');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al simular');
    } finally {
      setSimulating(false);
    }
  };

  const handleSave = async () => {
    if (!hasSimulation || detailed.length === 0) {
      toast.error('Primero debes simular para generar una distribución');
      return;
    }
    setSaving(true);
    try {
      await schedulerAPI.save({
        sede_id: selectedSede,
        week_start_date: weekStartDate,
        assignments: detailed
      });
      setHasSimulation(false);
      toast.success('Asignaciones guardadas y notificadas a los colaboradores');
      loadDetailed();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Group detailed by day
  const byDay = {};
  detailed.forEach(a => {
    if (!byDay[a.day_of_week]) byDay[a.day_of_week] = [];
    byDay[a.day_of_week].push(a);
  });

  // Compute week date labels
  const getWeekDayDate = (dayIndex) => {
    if (!weekStartDate) return '';
    try {
      const d = addDays(new Date(weekStartDate + 'T00:00:00'), dayIndex);
      return format(d, 'dd MMM', { locale: es });
    } catch { return ''; }
  };

  const selectedSedeName = sedes.find(s => s.id === selectedSede)?.name || '';

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Motor de Asignación
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Ejecuta el algoritmo de asignación semanal con equidad
          </p>
        </div>

        {/* Compact Config + Result Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Config - compact */}
          <div className="lg:col-span-5 flex items-center gap-3 p-4 border border-slate-200 rounded-lg bg-white">
            <div className="flex-1 min-w-0 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Sede</Label>
                  <Select value={selectedSede} onValueChange={setSelectedSede}>
                    <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="scheduler-sede-select">
                      <SelectValue placeholder="Seleccionar sede" />
                    </SelectTrigger>
                    <SelectContent>
                      {sedes.map((sede) => (
                        <SelectItem key={sede.id} value={sede.id}>{sede.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Semana inicio</Label>
                  <Input
                    type="date"
                    value={weekStartDate}
                    onChange={(e) => setWeekStartDate(e.target.value)}
                    className="rounded-sm h-9 text-sm"
                    data-testid="scheduler-week-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSimulate}
                  variant="outline"
                  className="rounded-sm h-9 px-4 text-sm border-[#002FA7] text-[#002FA7] hover:bg-blue-50"
                  disabled={simulating || !selectedSede}
                  data-testid="simulate-btn"
                >
                  {simulating ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Simulando...</>
                  ) : (
                    <><Play className="w-3.5 h-3.5 mr-1.5" />Simular</>
                  )}
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-[#002FA7] hover:bg-[#001B66] rounded-sm h-9 px-4 text-sm"
                  disabled={saving || !hasSimulation}
                  data-testid="execute-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Guardando...</>
                  ) : (
                    <><CheckCircle className="w-3.5 h-3.5 mr-1.5" />Ejecutar</>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Result - compact */}
          <div className="lg:col-span-7 p-4 border border-slate-200 rounded-lg bg-white">
            {hasSimulation ? (
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-50 rounded-sm border border-blue-200 text-center min-w-[80px]">
                    <p className="text-2xl font-bold text-blue-700">{detailed.length}</p>
                    <p className="text-[10px] text-blue-600">Asignados</p>
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{new Set(detailed.map(a => a.user_id)).size} colaboradores en {[0,1,2,3,4].filter(d => (byDay[d]||[]).length > 0).length} días</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-yellow-700">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Simulación en pantalla — presiona <strong className="mx-0.5">Ejecutar</strong> para guardar y notificar.
                    </div>
                  </div>
                </div>
              </div>
            ) : detailed.length > 0 ? (
              <div className="flex items-center gap-4">
                <div className="p-2 bg-green-50 rounded-sm border border-green-200 text-center min-w-[80px]">
                  <p className="text-2xl font-bold text-green-700">{detailed.length}</p>
                  <p className="text-[10px] text-green-600">Guardados</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 text-xs text-green-700">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Asignaciones activas para esta semana. Puedes simular una nueva distribución.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-center py-4">
                <div className="flex items-center gap-3 text-slate-400">
                  <Calendar className="w-8 h-8" />
                  <p className="text-sm">Presiona <strong>Simular</strong> para generar una distribución</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Weekly Assignments Table */}
        {loadingDetailed ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : detailed.length > 0 ? (
          <Card className={`border border-slate-200 ${fullscreen ? 'fixed z-[9999] rounded-none bg-white overflow-hidden' : ''}`} style={fullscreen ? { top: 0, left: 0, right: 0, bottom: 0, margin: 0 } : {}}>
            <CardHeader className={`pb-2 ${fullscreen ? 'py-2 px-4 border-b border-slate-200' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">
                    Detalle de Asignaciones — {selectedSedeName}
                    {hasSimulation && <span className="ml-2 text-yellow-600 font-medium">(Simulación — sin guardar)</span>}
                  </CardTitle>
                  <CardDescription>Semana del {getWeekDayDate(0)} al {getWeekDayDate(4)} · {detailed.length} asignaciones</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{new Set(detailed.map(a => a.user_id)).size} colaboradores</span>
                  <Button variant="outline" size="sm" onClick={() => setFullscreen(!fullscreen)} className="h-8 px-2" data-testid="fullscreen-toggle">
                    {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-5 divide-x divide-slate-200 border-t border-slate-200">
                {[0, 1, 2, 3, 4].map(day => {
                  const dayItems = byDay[day] || [];
                  return (
                    <div key={day} className="min-w-0">
                      <div className="bg-slate-50 px-3 py-2.5 border-b border-slate-200 sticky top-0 z-10">
                        <p className="font-semibold text-sm">{DAY_NAMES[day]}</p>
                        <p className="text-xs text-slate-500">{getWeekDayDate(day)} · {dayItems.length} personas</p>
                      </div>
                      <div className="divide-y divide-slate-100" style={{ maxHeight: fullscreen ? 'calc(100vh - 100px)' : 'calc(100vh - 380px)', overflowY: 'auto' }}>
                        {dayItems.length === 0 ? (
                          <p className="text-xs text-slate-400 p-4 text-center">Sin asignaciones</p>
                        ) : dayItems.map(a => (
                          <div key={a.id} className="p-3 hover:bg-slate-50/50 space-y-1.5">
                            <div className="flex items-center justify-between gap-1">
                              <p className="font-medium text-sm truncate">{a.user_name}</p>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${STATUS_STYLES[a.status] || 'bg-slate-100 text-slate-600'}`}>
                                {a.status === 'assigned' ? 'Asignado' : a.status === 'confirmed' ? 'Confirmado' : a.status}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              <p className="text-[11px] text-slate-500 flex items-center gap-1">
                                <Layers className="w-3 h-3 shrink-0" />
                                <span className="truncate">{a.floor !== '-' ? a.floor : 'Sin piso'}</span>
                              </p>
                              <p className="text-[11px] text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3 shrink-0" />
                                <span className="truncate">{a.zone !== '-' ? a.zone : 'Sin zona'}</span>
                              </p>
                              <p className="text-[11px] text-[#002FA7] font-medium flex items-center gap-1">
                                <span className="w-3 h-3 shrink-0 flex items-center justify-center text-[9px]">🪑</span>
                                <span className="truncate">{a.space !== '-' ? a.space : 'Sin espacio'}</span>
                              </p>
                            </div>
                            <p className="text-[10px] text-slate-400 truncate">{a.area} · {a.team}</p>
                            {a.equity_score && (
                              <div className="flex items-center gap-1">
                                <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#002FA7] rounded-full" style={{ width: `${Math.min(a.equity_score, 100)}%` }} />
                                </div>
                                <span className="text-[10px] text-slate-400">{a.equity_score}%</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : selectedSede && weekStartDate ? (
          <Card className="border border-slate-200">
            <CardContent className="py-12 text-center">
              <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">No hay asignaciones para esta semana. Ejecuta el scheduler.</p>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </AdminLayout>
  );
};

export default SchedulerPage;
